<template>
  <div class="main-container">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <SearchBar :sidebar-nav-collapsed="sidebarNavCollapsed" />
    <div
      class="reports-container"
      :class="{['reports-container-sidebar-collapse']: sidebarNavCollapsed}"
    >
      <div class="reports-sub-container">
        <div class="table-header">
          {{ $t('reports.title') }}
        </div>

        <div class="table-header-description">
          {{ $t('reports.description') }}
        </div>

        <div>
          <c-notification style="text-align: left">
            {{ $t('reports.noteHeading') }}
            <br />
            {{ $t('reports.noteDescription') }}
          </c-notification>
        </div>
        <br />

        <ReportTableFilterOption
          :disabled="loading.all"
          table-label="Reports"
          v-bind="tableFilterOptionConfig"
          @childApplyFilter="applyFilter"
          @childClearFilter="applyFilter"
          ref="tableFilterOptionComponent"
        />
        <div class="summary-container" v-if="!loading.all">
          <div class="summary-header" v-if="doesReportExist">
            <div class="summary-title">
              {{ $t('reports.heading', {month: getSummaryMonth, year: getSummaryYear}) }}
            </div>
            <c-button
              variant="primary-outline"
              :disabled="loading.all"
              :loading="loading.report"
              @click="downloadReport"
              class="primary-outline-btn"
            >
              <DownloadsIcon />
              {{ $t('reports.download') }}
            </c-button>
          </div>

          <div class="summary-table" v-if="doesReportExist">
            <c-table :variant="'stroke'">
              <c-tr v-for="summaryMetric in reportSummaryMetrics" :key="summaryMetric[0]">
                <c-th class="title-cell">
                  {{ $t('reports.summary.' + summaryMetric[0]) }}
                  <ToolTip
                    placement="right"
                    v-if="summaryMetric[0] === 'deposits'"
                    :content="$t('reports.summary.tooltip.deposits')"
                  >
                    <img alt="dropdown" class="statistics-info-icon" src="@/app/assets/images/info-outline.svg" />
                  </ToolTip>
                </c-th>
                <c-th class="value-cell">
                  {{ formatAmount(summaryMetric[1], summaryMetric[0]) }} {{ getMerchantCurrency }}
                </c-th>
              </c-tr>
            </c-table>
          </div>
          <div class="empty-container" v-if="!doesReportExist">
            <EmptyPlaceholder
              :heading-tag="$t('reports.noReportsYetTitle')"
              :description-tag="$t('reports.noReportsYetDesc')"
              :clear-filter-flag="false"
            >
              <div slot="icon">
                <FolderIcon class="empty-placeholder-icon" />
              </div>
            </EmptyPlaceholder>
          </div>
        </div>
        <div class="loading-message" v-else>
          <div class="summary-table">
            <c-table variant="stroke">
              <c-tr v-for="index in 6" :key="index">
                <c-th>
                  <div class="line loading-shimmer"></div>
                </c-th>
                <c-th>
                  <div class="value-shimmer">
                    <div class="line loading-shimmer"></div>
                  </div>
                </c-th>
              </c-tr>
            </c-table>
          </div>
        </div>
        <PortalFooter />
      </div>
    </div>
  </div>
</template>

<script>
import ExcelJS from 'exceljs';
import axios from 'axios';
import SidebarNav from "@/components/SidebarNav.vue";
import SearchBar from "@/components/SearchBar.vue";
import ReportTableFilterOption from "@/components/common/ReportTableFilterOption";
import reports from "@/api/reports";
import {ReportType} from "@/api/paymentApi";
import {mapGetters} from "vuex";
import {portalCheckMixin} from "@/mixin/portalPageCheck";
import Formatter from "@/app/utils/common/functions/formatter";
import PortalFooter from "@/components/portal/PortalFooter";
import EmptyPlaceholder from "@/components/transactions/EmptyPlaceholder.vue";
import FolderIcon from "@/components/icons/FolderIcon";
import DownloadsIcon from "@/components/icons/DownloadsIcon.vue";
import {getDateTimeFromJSDate, getDateTimeNow,} from "@/app/utils/common/functions/dateUtils";
import ToolTip from "@/components/common/ToolTip.vue";
import {ICPlusPlusReport} from "@/views/reports/ICPlusPlusReport";
import {BlendedReport} from "@/views/reports/BlendedReport";
import {BlendedAndICReport} from "@/views/reports/BlendedAndICReport";
import {NonCardTransactionsReport} from "@/views/reports/NonCardTransactionsReport";
import "@/app/assets/style/reports.scss"

const CSV_DELIMITER_FOR_REPORTS = ';';
const FEE = "FEE";
const BLENDED_REPORT = "BLENDED_REPORT";
const IC_PLUS_PLUS_REPORT = "IC_PLUS_PLUS_REPORT";
const BLENDED_AND_IC_PLUS_PLUS_REPORT = "BLENDED_AND_IC_PLUS_PLUS_REPORT";
const NON_CARD_TRANSACTIONS_REPORT = "NON_CARD_TRANSACTIONS_REPORT";

export default {
  name: 'ReportView',
  components: {
    ToolTip,
    DownloadsIcon,
    SearchBar, SidebarNav, ReportTableFilterOption, PortalFooter, EmptyPlaceholder, FolderIcon
  },
  mixins: [portalCheckMixin],
  data() {
    return {
      report: null,
      doesReportExist: false,
      reportType: ReportType.CONSOLIDATED,
      sidebarNavCollapsed: false,
      summaryDetails: null,
      payoutDetails: null,
      disputeDetails: null,
      refundReserveDetails: null,
      chargebackReserveDetails: null,
      transactionDetails: null,
      loading: {
        all: false,
        report: false,
      },
      startDate: null,
      endDate: null,
      tableFilterOptionConfig: {},
      reportTemplate: BLENDED_REPORT,
      reportManager: null, // Initialize the class
    };
  },
  computed: {
    ...mapGetters('app', ['getMerchantId', 'getMerchantCreatedAt', 'isAdminMode', 'getMerchantCurrency']),
    reportSummary() {
      return JSON.parse(this.report?.summary || null);
    },
    reportSummaryMetrics() {
      const summaryMetrics = ["GROSS_VOLUME", FEE, "NET_VOLUME", "DEPOSITS", "REFUNDS", "CHARGEBACKS"];
      const orderedEntries = summaryMetrics.map(key => [this.getReportSummaryKey(key), this.reportSummary?.[key]]);
      return new Map(orderedEntries);
    },
    getSummaryMonth() {
      return getDateTimeFromJSDate(this.startDate)?.monthLong;
    },
    getSummaryYear() {
      return getDateTimeFromJSDate(this.startDate)?.year;
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.initializeDates();
      if (!this.doesReportExist) {
        await this.fetchReportAndSummary(false);
      }
    },
    async fetchReportAndSummary(showErrorToast = true) {
      this.loading.all = true;
      try {
        this.report = await reports.fetchReport(
          this.getMerchantId,
          this.reportType,
          this.startDate,
          this.endDate,
        );
        this.doesReportExist = true;
      } catch (e) {
        if (showErrorToast) {
          // TODO: Fix this part of other timezones like 'Pacific/Kwajalein'
          this.$danger(this.$t('reports.reportNotFoundError', {month: this.startDate
                ?.toLocaleString('default', {month: 'long'}), reportType: this.reportType}),
            {dismissable: true, timeout: 4000});
        }
        this.doesReportExist = false;
      } finally {
        this.loading.all = false;
      }
    },
    initializeDates() {
      this.startDate = this.getDefaultStartDate().toJSDate();
      this.endDate = this.getDefaultEndDate().toJSDate();
    },
    resetReportDetails() {
      this.summaryDetails = this.payoutDetails = this.transactionDetails = this.disputeDetails =
      this.refundReserveDetails = this.chargebackReserveDetails = null;
    },
    getDefaultStartDate() {
      const currentDate = getDateTimeNow();
      return currentDate.minus({ months: 1 }).startOf('month');
    },
    getDefaultEndDate() {
      const currentDate = getDateTimeNow();
      return currentDate.startOf('month');
    },
    getReportSummaryKey(key) {
      return key.toLowerCase();
    },
    applyFilter(filter) {
      let startDateTime = this.getStartDateTime(filter.currentMonthFilter);
      this.startDate = startDateTime.toJSDate();
      this.endDate = this.getNextMonthStartDate(startDateTime).toJSDate();
      this.fetchReportAndSummary();
    },
    getStartDateTime(monthYear) {
      const [month, year] = monthYear.split("-");
      const monthIndex = new Date(`01 ${month}, ${year}`).getMonth() + 1;
      const startDate = this.getDefaultStartDate();
      return startDate.set({ month: monthIndex, year: year });
    },
    getNextMonthStartDate(givenDate) {
      return givenDate.plus({months: 1});
    },
    formatAmount(amount, summaryMetric) {
      if (summaryMetric === 'fee')
        amount = amount * -1;
      return Formatter.format({ type: "currency", data: amount, currencyCode: this.getMerchantCurrency });
    },
    async downloadReport() {
      try {
        this.loading.report = true;
        const downloadedReportDetails = await reports.downloadReport(this.getMerchantId, this.report.id);
        await this.downloadReportDetails(downloadedReportDetails);
        this.$success(this.$t('reports.successToast'));
      } catch (e) {
        this.$danger("An error occurred while downloading the report.");
      } finally {
        this.loading.report = false;
      }
    },
    async downloadReportDetails(downloadedReportDetails) {
      this.resetReportDetails();
      if (downloadedReportDetails.summary_url) {
        const summaryResponse = await this.getReportDataFromUrl(downloadedReportDetails.summary_url);
        this.summaryDetails = this.parseCsvData(summaryResponse);
        this.reportTemplate = this.summaryDetails[1][1];
        this.summaryDetails.splice(1, 1);
        this.getReportType();
      }
      if (downloadedReportDetails.payouts_url) {
        const payoutResponse = await this.getReportDataFromUrl(downloadedReportDetails.payouts_url);
        this.payoutDetails = this.parseCsvData(payoutResponse);
        this.reportManager?.setPayoutDetails(this.payoutDetails);
      }
      if (downloadedReportDetails.transactions_url) {
        const transactionsResponse = await this.getReportDataFromUrl(downloadedReportDetails.transactions_url);
        this.transactionDetails = this.parseCsvData(transactionsResponse);
        this.reportManager?.setTransactionDetails(this.transactionDetails);
      }
      if (downloadedReportDetails.disputes_url) {
        const disputesResponse = await this.getReportDataFromUrl(downloadedReportDetails.disputes_url);
        this.disputeDetails = this.parseCsvData(disputesResponse);
      }
      if (downloadedReportDetails.refund_reserve_url) {
        const refundReserveResponse = await this.getReportDataFromUrl(downloadedReportDetails.refund_reserve_url);
        this.refundReserveDetails = this.parseCsvData(refundReserveResponse);
      }
      if (downloadedReportDetails.chargeback_reserve_url) {
        const chargebackReserveResponse = await this.getReportDataFromUrl(
            downloadedReportDetails.chargeback_reserve_url);
        this.chargebackReserveDetails = this.parseCsvData(chargebackReserveResponse);
      }
      await this.processExcelFile();
    },
    async getReportDataFromUrl(url) {
      const response = await axios.get(url, { responseType: 'text' });
      return response.data;
    },
    parseCsvData(csvData) {
      const csvEndOfFileRegex = /\r?\n$/i;
      const cleanedCsvData = csvData.replace(csvEndOfFileRegex, '');
      const lines = cleanedCsvData.split(/\r?\n/);
      return lines.map(entry => entry.split(CSV_DELIMITER_FOR_REPORTS));
    },
    async processExcelFile() {
      const workbook = new ExcelJS.Workbook();
      const month = this.getSummaryMonth;
      const year = this.getSummaryYear;

      if (this.summaryDetails) {
        const summarySheet = workbook.addWorksheet('Summary');
        this.createSummarySheet(summarySheet, month, year);
      }

      // Transactions, payouts, disputes and reserve sheets need the report manager to resolve their
      // headers. If the template was unknown/unsupported, reportManager is null, so skip these sheets
      // (the summary above still renders) instead of crashing the whole download.
      if (this.transactionDetails && this.reportManager) {
        const transactionsSheet = workbook.addWorksheet('Transactions');
        const transactionHeaders = this.reportManager.getTransactionHeaders();
        // Backward compatibility: only show the settlement-currency note for the newer report layout
        // (which has the "Settlement Currency" column). Older reports predate settlement-currency
        // rendering, so the note would be inaccurate for them.
        const settlementCurrencyNote = transactionHeaders.includes('Settlement Currency')
            ? this.$t('reports.transactionsSettlementCurrencyNote')
            : null;
        this.createDataSheet(
            transactionsSheet,
            this.transactionDetails,
            transactionHeaders,
            `Transactions Report ${month} ${year}`,
            settlementCurrencyNote
        );
      }

      if (this.payoutDetails && this.reportManager) {
        const payoutsSheet = workbook.addWorksheet('Payouts');
        this.createDataSheet(
            payoutsSheet,
            this.payoutDetails,
            this.reportManager.getPayoutHeaders(),
            `Payouts Report ${month} ${year}`
        );
      }

      if (this.disputeDetails && this.reportManager) {
        const disputesSheet = workbook.addWorksheet('Disputes');
        this.createDataSheet(
            disputesSheet,
            this.disputeDetails,
            this.reportManager.getDisputeHeaders(),
            `Disputes Report ${month} ${year}`
        );
      }

      if (this.refundReserveDetails && this.reportManager) {
        const refundReserveSheet = workbook.addWorksheet('Refund Reserve');
        this.createDataSheet(
            refundReserveSheet,
            this.refundReserveDetails,
            this.reportManager.getReserveHeaders(),
            `Refund Reserve Report ${month} ${year}`
        );
      }

      if (this.chargebackReserveDetails && this.reportManager) {
        const chargebackReserveSheet = workbook.addWorksheet('Chargeback Reserve');
        this.createDataSheet(
            chargebackReserveSheet,
            this.chargebackReserveDetails,
            this.reportManager.getReserveHeaders(),
            `Chargeback Reserve Report ${month} ${year}`
        );
      }

      await this.saveWorkbookAsFile(workbook, 'consolidated_report' + '_' + month + '_' + year + '.xlsx');
    },

    createSummarySheet(worksheet, month, year) {
      // Excel layout: Row 1 is empty for top margin, Row 2 has title, Row 3 is spacing, Row 4 starts data
      const titleRowNumber = 2; // Row 2 for the title
      const startColumn = 2; // Column B (Column A is left empty for left margin)
      const dataStartRow = 4; // Row 4 where summary data begins
      // Dynamic column count: 2 for single-currency, N+2 for multi-currency (metric + Overall + per-currency cols)
      const summaryColumns = this.summaryDetails?.[0]?.length || 2;

      const title = `${this.$t('reports.heading', { month, year })}`;
      worksheet.mergeCells(titleRowNumber, startColumn, titleRowNumber, startColumn + summaryColumns - 1);
      const titleCell = worksheet.getCell(titleRowNumber, startColumn);
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      titleCell.font = { bold: true, size: 14 };
      titleCell.value = title;

      // Fill summary data starting from row 4, column B, with 2 columns (metric name and value)
      const lastDataRow = this.fillValuesInCell(dataStartRow, startColumn, summaryColumns,
          this.summaryDetails, worksheet, true);

      // Add disclaimer text after the table
      this.addSummaryDisclaimer(worksheet, lastDataRow, startColumn, summaryColumns);

      // Set minimum column width for readability
      const minColumnWidth = 15;
      for (const column of worksheet.columns) {
        column.width = Math.max(column.width || 10, minColumnWidth);
      }
    },

    addSummaryDisclaimer(worksheet, lastDataRow, startColumn, summaryColumns) {
      // Add disclaimer 2 rows after the last data row for spacing
      const disclaimerRow = lastDataRow + 1;
      
      const disclaimerText = this.$t('reports.disclaimer');
      
      // Merge cells for the disclaimer across both columns
      worksheet.mergeCells(disclaimerRow, startColumn, disclaimerRow, startColumn + summaryColumns - 1);
      
      const disclaimerCell = worksheet.getCell(disclaimerRow, startColumn);
      disclaimerCell.value = disclaimerText;
      disclaimerCell.alignment = { 
        vertical: 'top', 
        horizontal: 'left',
        wrapText: true 
      };
      disclaimerCell.font = { 
        italic: true, 
        size: 10 
      };
      
      // Set row height to accommodate wrapped text
      worksheet.getRow(disclaimerRow).height = 40;
    },

    createDataSheet(worksheet, data, headers, title, note = null) {
      // Excel layout: Row 1 is empty for top margin,Row 2 has title,Row 3 is spacing,Row 4 has headers,Row 5+ has data
      const titleRowNumber = 2; // Row 2 for the sheet title
      const headerRowNumber = 4; // Row 4 for column headers
      const dataStartRow = 5; // Row 5 where actual data begins
      const startColumn = 2; // Column B (Column A is left empty for left margin)

      worksheet.mergeCells(titleRowNumber, startColumn, titleRowNumber, startColumn + headers.length - 1);
      const titleCell = worksheet.getCell(titleRowNumber, startColumn);
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      titleCell.font = { bold: true, size: 14 };
      titleCell.value = title;

      // Fill header row with column names
      this.fillValuesInHeader(
          headerRowNumber,
          startColumn,
          headers.length,
          headers,
          worksheet,
          true // makeBold parameter
      );

      // Fill data rows starting from row 5
      const lastDataRow = this.fillValuesInCell(dataStartRow, startColumn, headers.length, data, worksheet);

      // Optional note/disclaimer below the data (e.g. clarifying the currency of the columns)
      if (note) {
        this.addSheetNote(worksheet, lastDataRow, startColumn, headers.length, note);
      }

      // Set minimum column width for readability
      const minColumnWidth = 15;
      for (const column of worksheet.columns) {
        column.width = Math.max(column.width || 10, minColumnWidth);
      }
    },

    addSheetNote(worksheet, lastDataRow, startColumn, numberOfColumns, note) {
      // Add the note 1 row after the last data row for spacing, merged across all columns.
      const noteRow = lastDataRow + 1;
      worksheet.mergeCells(noteRow, startColumn, noteRow, startColumn + numberOfColumns - 1);

      const noteCell = worksheet.getCell(noteRow, startColumn);
      noteCell.value = note;
      noteCell.alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true
      };
      noteCell.font = {
        italic: true,
        size: 10
      };
      worksheet.getRow(noteRow).height = 20;
    },

    fillValuesInCell(startRow, startColumn, numberOfColumns, arrayData, worksheet, isSummarySheet = false) {
      // Start from index 1 to skip the header row in arrayData (index 0 contains data types/headers)
      let actualRow = startRow;
      for (let rowIndex = 1; rowIndex < arrayData.length; rowIndex++) {
        actualRow = this.fillRowCells(actualRow, startColumn, numberOfColumns, arrayData, rowIndex, worksheet,
            isSummarySheet);
        actualRow = this.insertEmptyRowIfNeeded(actualRow, arrayData, rowIndex, isSummarySheet);
        actualRow++;
      }
      return actualRow;
    },

    fillRowCells(actualRow, startColumn, numberOfColumns, arrayData, rowIndex, worksheet, isSummarySheet) {
      for (let colIndex = 0, col = startColumn; colIndex < numberOfColumns; colIndex++, col++) {
        const cell = worksheet.getCell(actualRow, col);
        const cellValue = arrayData[rowIndex][colIndex];
        const dataType = arrayData[0][colIndex];
        const cellStyle = this.createCellStyle(cell);
        
        // Special handling for reserve fields with zero values
        if (this.shouldHandleReserveZeroValue(isSummarySheet, colIndex, arrayData, rowIndex, cellValue)) {
          this.applyReserveZeroValueFormat(cell, cellStyle, worksheet, col);
          continue;
        }
        
        this.applyDefaultCellFormat(cell, cellValue, dataType, cellStyle, worksheet, col);
      }
      return actualRow;
    },

    shouldHandleReserveZeroValue(isSummarySheet, colIndex, arrayData, rowIndex, cellValue) {
      if (!isSummarySheet || colIndex !== 1) {
        return false;
      }
      
      const fieldName = arrayData[rowIndex][0]?.toString().toLowerCase() || '';
      const isReserveField = fieldName.includes('refund') && fieldName.includes('reserve') ||
                             fieldName.includes('chargeback') && fieldName.includes('reserve');
      
      if (!isReserveField) {
        return false;
      }
      
      const numericValue = Number.parseFloat(cellValue);
      return cellValue === '' || cellValue === '-' || cellValue === null || 
             cellValue === undefined || (!Number.isNaN(numericValue) && numericValue === 0);
    },

    applyReserveZeroValueFormat(cell, cellStyle, worksheet, col) {
      cell.value = 0;
      cellStyle.numFmt = '_(* 0.00_);_(* (0.00);_(* 0.00_);_(@_)';
      cell.style = cellStyle;
      worksheet.getColumn(col).width = Math.max(worksheet.getColumn(col).width || 10, 15);
    },

    applyDefaultCellFormat(cell, cellValue, dataType, cellStyle, worksheet, col) {
      cell.value = Formatter.formatReportDataForExcel(cellValue, dataType);
      const excelDataFormat = Formatter.setNumberFormattingForExcel(dataType);
      if (excelDataFormat !== null) {
        cellStyle.numFmt = excelDataFormat;
      }
      cell.style = cellStyle;
      // Add padding to cell width (5 extra characters for comfortable spacing).
      // Guard against missing cells: older reports (downloaded from AWS) may have fewer columns
      // than the current header set, so cellValue can be undefined for the trailing columns.
      const widthPadding = 5;
      const calculatedWidth = (cellValue ?? '').toString().length + widthPadding;
      const minWidth = 10;
      worksheet.getColumn(col).width = Math.max(worksheet.getColumn(col).width || minWidth, calculatedWidth);
    },

    createCellStyle(cell) {
      return {
        ...cell.style,
        border: {
          ...cell.style.border,
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        },
        alignment: {
          ...cell.style.alignment,
          vertical: 'middle',
          horizontal: 'center'
        },
      };
    },

    insertEmptyRowIfNeeded(actualRow, arrayData, rowIndex, isSummarySheet) {
      if (!isSummarySheet || rowIndex >= arrayData.length) {
        return actualRow;
      }
      
      const fieldName = arrayData[rowIndex][0]?.toString().toLowerCase() || '';
      if (fieldName.includes('pending') && fieldName.includes('payout')) {
        return actualRow + 1;
      }
      
      return actualRow;
    },

    fillValuesInHeader(row, startColumn, numberOfColumns, arrayData, worksheet, makeBold) {
      for (let colIndex = 0, col = startColumn; colIndex < numberOfColumns; colIndex++, col++) {
        let cell = worksheet.getCell(row, col);
        const cellStyle = {
          ...cell.style,
          border: {
            ...cell.style.border,
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          },
          alignment: {
            ...cell.style.alignment,
            vertical: 'middle',
            horizontal: 'center'
          },
          font: {
            ...cell.style.font,
            bold: makeBold
          }
        };
        cell.value = arrayData[colIndex];
        cell.style = cellStyle;
        // Add padding to header width (5 extra characters for comfortable spacing)
        const widthPadding = 5;
        const valueWidth = arrayData[colIndex].length + widthPadding;
        const minWidth = 10;
        worksheet.getColumn(col).width = Math.max(worksheet.getColumn(col).width || minWidth, valueWidth);
      }
    },
    getReportType() {
      // Reset before resolving so a manager from a previous download can't supply incorrect
      // headers, and an unknown/unsupported template leaves reportManager null.
      this.reportManager = null;
      switch(this.reportTemplate) {
        case IC_PLUS_PLUS_REPORT:
          this.reportManager = new ICPlusPlusReport();
          break;
        case BLENDED_REPORT:
          this.reportManager = new BlendedReport();
          break;
        case BLENDED_AND_IC_PLUS_PLUS_REPORT:
          this.reportManager = new BlendedAndICReport();
          break;
        case NON_CARD_TRANSACTIONS_REPORT:
          this.reportManager = new NonCardTransactionsReport();
          break;
        default:
          return null;
      }
    },
    async saveWorkbookAsFile(workbook, fileName) {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = fileName;
      downloadLink.click();
    },
  },
};
</script>

<style lang="scss">


</style>
